import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, AlertTriangle, Calendar as CalendarIcon, Zap, ChevronRight, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORIDADE_CORES = { baixa: 'bg-slate-500', media: 'bg-blue-500', alta: 'bg-orange-500', urgente: 'bg-red-500' };
const STATUS_LABELS = { pendente: 'Pendente', em_andamento: 'Em andamento', atrasado: 'Atrasado', adiado: 'Adiado' };

const fmtDate = (str) => {
  if (!str) return '';
  const [y, m, d] = String(str).split('T')[0].split('-');
  return `${d}/${m}`;
};

export default function LembretesWidget({ compact = false }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ hoje: 0, proximos: 0, atrasados: 0, urgentes: 0, concluidos_mes: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [sRes, lRes] = await Promise.all([
        axios.get(`${API}/api/reminders/stats`),
        axios.get(`${API}/api/reminders?limit=6`),
      ]);
      setStats(sRes.data || {});
      setItems(lRes.data || []);
    } catch (e) {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const concluir = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/api/reminders/${id}/concluir`);
      toast.success('Concluído');
      fetchData();
    } catch (err) {
      toast.error('Erro ao concluir');
    }
  };

  const irPara = (preset) => {
    navigate(`/lembretes${preset ? `?preset=${preset}` : ''}`);
  };

  return (
    <Card data-testid="lembretes-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Lembretes
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => irPara()} data-testid="dashboard-lembretes-ver-todos">
            Ver todos <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 4 mini-stats clicáveis */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            onClick={() => irPara('atrasados')}
            className="rounded-lg border p-2 text-left transition hover:bg-red-500/5 border-red-500/30"
            data-testid="widget-ind-atrasados"
          >
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <AlertTriangle className="h-3 w-3 text-red-500" /> Atrasados
            </div>
            <p className="text-xl sm:text-2xl font-bold text-red-500">{stats.atrasados || 0}</p>
          </button>
          <button
            onClick={() => irPara('hoje')}
            className="rounded-lg border p-2 text-left transition hover:bg-blue-500/5 border-blue-500/30"
            data-testid="widget-ind-hoje"
          >
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <CalendarIcon className="h-3 w-3 text-blue-500" /> Hoje
            </div>
            <p className="text-xl sm:text-2xl font-bold text-blue-500">{stats.hoje || 0}</p>
          </button>
          <button
            onClick={() => irPara('urgentes')}
            className="rounded-lg border p-2 text-left transition hover:bg-orange-500/5 border-orange-500/30"
            data-testid="widget-ind-urgentes"
          >
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Zap className="h-3 w-3 text-orange-500" /> Urgentes
            </div>
            <p className="text-xl sm:text-2xl font-bold text-orange-500">{stats.urgentes || 0}</p>
          </button>
          <button
            onClick={() => irPara('semana')}
            className="rounded-lg border p-2 text-left transition hover:bg-purple-500/5 border-purple-500/30"
            data-testid="widget-ind-proximos"
          >
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3 text-purple-500" /> Próximos 7d
            </div>
            <p className="text-xl sm:text-2xl font-bold text-purple-500">{stats.proximos || 0}</p>
          </button>
        </div>

        {/* Lista compacta (oculta em modo compact) */}
        {!compact && (
          loading ? (
            <div className="text-center text-sm text-muted-foreground py-4">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">Nenhum lembrete ativo.</div>
          ) : (
            <div className="space-y-1">
              {items.slice(0, 5).map(r => {
                const prioCor = PRIORIDADE_CORES[r.prioridade] || 'bg-blue-500';
                const isAtrasado = r.status === 'atrasado';
                return (
                  <button
                    key={r.id}
                    onClick={() => navigate('/lembretes')}
                    className={`w-full flex items-center gap-2 p-2 rounded border text-left hover:bg-muted/50 transition ${isAtrasado ? 'border-red-500/40' : ''}`}
                    data-testid={`widget-lembrete-${r.id}`}
                  >
                    <div className={`w-1 self-stretch min-h-[24px] rounded ${prioCor}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.titulo}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        {r.data && <span>{fmtDate(r.data)}{r.horario ? ` ${r.horario}` : ''}</span>}
                        <span className={isAtrasado ? 'text-red-500 font-semibold' : ''}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </div>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={(e) => concluir(r.id, e)}
                      className="h-7 w-7 shrink-0"
                      title="Concluir"
                      data-testid={`widget-concluir-${r.id}`}
                    >
                      <Check className="h-4 w-4 text-green-600" />
                    </Button>
                  </button>
                );
              })}
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}
